/**
 * Client scope — read from the URL, and only from the URL.
 *
 * ND-C1 (owner, 2026-09-07): a client-owned screen lives at
 * `/clients/:id/<screen>`. There is no persisted "current client", and this
 * module must never become one. The cache below is a *request* cache keyed by
 * the id already in the path — it answers "what is this client called", never
 * "which client am I looking at". That second question has exactly one answer,
 * and it is the address bar.
 *
 * Why that matters here rather than in a comment somewhere: the most expensive
 * mistake available in this product is right numbers, wrong client. A stored
 * current-client makes that mistake invisible in a screenshot, a bug report and
 * a second browser tab.
 */

import { useEffect, useState } from 'react'
import { useMatches } from 'react-router'

import { ApiError, type ClientRecord, clients } from './api'

/** Screens that live inside a client. The order is the sidebar's. */
export const CLIENT_SCREENS = [
  'intake',
  'review',
  'returns',
  'gstr3b',
  'reconciliation',
  'filed-compare',
  'tb-recon',
  'reports',
  'download',
  'activity',
] as const

export type ClientScreen = (typeof CLIENT_SCREENS)[number]

export function clientPath(id: string, screen?: ClientScreen): string {
  return screen ? `/clients/${id}/${screen}` : `/clients/${id}`
}

/**
 * The client id in the current URL, or null when we are not inside a client.
 * `useMatches` rather than `useParams` so the chrome — which renders *above*
 * the client routes — can read it too.
 */
export function useClientId(): string | null {
  const matches = useMatches()
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const params = matches[i]?.params as Record<string, string | undefined> | undefined
    if (params?.id) return params.id
  }
  return null
}

// One in-flight/settled read per id, shared by the chrome and the layout so a
// single navigation does not fetch the same client twice.
const cache = new Map<string, Promise<ClientRecord>>()

export function invalidateClient(id: string): void {
  cache.delete(id)
}

function read(id: string): Promise<ClientRecord> {
  let hit = cache.get(id)
  if (!hit) {
    hit = clients.get(id).then((body) => body.data)
    cache.set(id, hit)
    // A failed read must not be remembered as a failure for the life of the
    // tab — a 500 during a deploy would otherwise persist past the fix.
    hit.catch(() => cache.delete(id))
  }
  return hit
}

export interface ScopedClient {
  client: ClientRecord | null
  loading: boolean
  /** The id is well-formed but no such client is ours — NOT_FOUND, by design. */
  missing: boolean
  error: string | null
}

export function useScopedClient(): ScopedClient {
  const id = useClientId()
  const [state, setState] = useState<ScopedClient>({
    client: null,
    loading: Boolean(id),
    missing: false,
    error: null,
  })

  useEffect(() => {
    if (!id) {
      setState({ client: null, loading: false, missing: false, error: null })
      return
    }
    let live = true
    setState((s) => ({ ...s, loading: true, missing: false, error: null }))
    read(id)
      .then((client) => {
        if (live) setState({ client, loading: false, missing: false, error: null })
      })
      .catch((err: unknown) => {
        if (!live) return
        const missing = err instanceof ApiError && err.code === 'NOT_FOUND'
        setState({
          client: null,
          loading: false,
          missing,
          error: missing ? null : err instanceof ApiError ? err.message : 'That client could not be loaded.',
        })
      })
    return () => {
      live = false
    }
  }, [id])

  return state
}
